'use client';

import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Clock, TrendingUp, FileText, CalendarCheck, ShieldCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const benefits = [
  {
    icon: MessageSquare,
    value: "80%",
    title: "Menos mensagens repetitivas",
    description: "Redução alcançada em até 15 dias de uso.",
  },
  {
    icon: Clock,
    value: "3–4h",
    title: "Horas diárias recuperadas",
    description: "Tempo livre para focar em pacientes e cirurgias.",
  },
  {
    icon: TrendingUp,
    value: "+30%",
    title: "Aumento de receita mensal",
    description: "Com mais tempo disponível para realizar novas cirurgias.",
  },
  {
    icon: FileText,
    value: "89%",
    title: "Redução no risco jurídico",
    description: "Com toda a comunicação documentada automaticamente.",
  },
  {
    icon: CalendarCheck,
    value: "100%",
    title: "Consultas organizadas",
    description: "Com confirmações e lembretes automáticos via WhatsApp.",
  },
  {
    icon: ShieldCheck,
    value: "24/7",
    title: "Segurança reforçada",
    description: "Notificações imediatas na plataforma em casos de risco.",
  },
];

const benefitsContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2 },
  },
  exit: { opacity: 0, transition: { staggerChildren: 0.1, staggerDirection: -1 } },
};

const benefitsItemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5 },
  },
  exit: { opacity: 0, y: 30, transition: { duration: 0.3 } },
};

const delayedItemVariants = (delay: number) => ({
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      delay,
    },
  },
  exit: { opacity: 0, y: 30, transition: { duration: 0.3, delay: delay * 0.5 } },
});

const benefitCardVariants = (delay: number) => ({
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay,
      },
    },
    exit: { opacity: 0, y: 30, transition: { duration: 0.3, delay: delay * 0.5 } },
  });

type BenefitsProps = {
  isBot: boolean;
};

export default function Benefits({ isBot }: BenefitsProps) {
  const Wrapper = isBot ? "div" : motion.div;
  return (
    <section id="benefits" className="py-10 sm:py-16 bg-secondary/50">
      <div
        className="container mx-auto px-4"
      >
        <Wrapper 
          initial={isBot ? undefined : "hidden"}
          whileInView={isBot ? undefined : "visible"}
          exit={isBot ? undefined : "exit"}
          viewport={isBot ? undefined : { once: false, amount: 0.2 }}
          variants={isBot ? undefined : benefitsContainerVariants}
          className="mx-auto max-w-2xl text-center"
        >
            <Wrapper
              variants={isBot ? undefined : benefitsItemVariants}
            >
              <h2 className="font-headline text-3xl tracking-[-0.04em] leading-[1.1] text-black sm:text-4xl font-semibold">
                Benefícios diretos, resultados imediatos
              </h2>
            </Wrapper>
            <Wrapper
                variants={isBot ? undefined : delayedItemVariants(0.2)}
            >
              <p className="mt-4 text-lg text-foreground/70 tracking-tight">
                Veja o impacto que MedFollow pode ter na sua rotina e no seu faturamento.
              </p>
            </Wrapper>
        </Wrapper>
        <div className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((benefit, index) => (
            <Wrapper
              key={index}
              custom={index}
              initial={isBot ? undefined : "hidden"}
              whileInView={isBot ? undefined : "visible"}
              exit={isBot ? undefined : "exit"}
              viewport={isBot ? undefined : { once: false, amount: 0.3 }}
              variants={isBot ? undefined : benefitCardVariants(0.4 + index * 0.1)}
            >
              <Card className={cn("h-full bg-card border-border hover:border-primary hover:shadow-lg transition-all transform hover:-translate-y-1", index % 2 === 0 ? "message-bubble" : "message-bubble-right")}>
                <CardHeader>
                  <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                          <benefit.icon className="h-6 w-6 text-primary" />
                      </div>
                      <p className="text-4xl font-bold text-primary">{benefit.value}</p>
                  </div>
                  <CardTitle className="pt-4 font-semibold text-foreground tracking-tight">{benefit.title}</CardTitle>
                  <CardDescription className="tracking-tight">{benefit.description}</CardDescription>
                </CardHeader>
              </Card>
            </Wrapper>
          ))}
        </div>
      </div>
    </section>
  );
}
